/**
 * Defense-in-depth sanitizer for assistant text emitted by the Claude Agent
 * SDK driver.
 *
 * The model occasionally leaks control markup into the *plaintext* of an
 * assistant `text` block:
 *
 * 1. A verbatim function-call XML wrapper
 *    (`[function_calls] … [invoke name="Bash"] … [/antml:invoke] … [/function_calls]`)
 *    — the same call is *also* emitted as a structured `tool_use` block, so the
 *    inline copy is pure duplication.
 * 2. A fabricated follow-up turn the model auto-completes from the prepended
 *    conversation transcript, e.g. a trailing `[User]: "…"` (or `[Assistant]:`)
 *    line. This hallucinated turn swallows the user's real next message in the
 *    UI.
 *
 * Neither belongs in persisted assistant prose. The primary fix lives in the
 * prompt shape (`enrichPromptWithHistory` no longer renders a continuable
 * `[User]:`/`[Assistant]:` transcript); this strips the residue if a leak still
 * slips through. It is deliberately conservative — only the clearly-leaked
 * control markup above is removed; legitimate prose (including a user *quoting*
 * a bracketed phrase mid-sentence) is left untouched.
 *
 * @module
 */

/**
 * Optional XML namespace prefix on a leaked tag name. The model emits tool
 * calls with a namespaced close (`[/antml:invoke]`, `[/antml:function_calls]`) and a
 * sometimes-namespaced open, so every tag matcher tolerates a leading
 * `<ns>:` (e.g. `antml:`). Written as a source fragment so the block and the
 * streaming-tail/opener detectors stay in sync.
 */
const NS = "(?:[a-z][a-z0-9]*:)?";

/**
 * Matches a leaked function-call XML wrapper. The model writes the literal
 * tag names `function_calls` / `invoke` / `parameter` in square brackets when
 * it echoes a tool call as text. Match the whole `[function_calls] … [/function_calls]`
 * span (non-greedy, dot-all) so nested `[invoke]`/`[parameter]` content goes
 * with it. Case-insensitive; tolerant of whitespace and an `antml:` namespace.
 */
const FUNCTION_CALL_BLOCK = new RegExp(
  `\\[\\s*${NS}function_calls\\s*\\][\\s\\S]*?\\[\\s*/\\s*${NS}function_calls\\s*\\]`,
  "gi",
);

/**
 * A stray `[invoke name="…"] … [/antml:invoke]` block without its enclosing
 * `[function_calls]` wrapper (the model sometimes leaks only the inner span).
 * Matched separately so a partial leak is still removed.
 */
const INVOKE_BLOCK = new RegExp(
  `\\[\\s*${NS}invoke\\b[\\s\\S]*?\\[\\s*/\\s*${NS}invoke\\s*\\]`,
  "gi",
);

/**
 * A fabricated role turn the model appends after its real answer. Anchored to
 * the *start of a line* and only stripped when it sits in the trailing portion
 * of the text (see {@link sanitizeAssistantText}). The role label is followed
 * by a colon, matching the `[User]:` / `[Assistant]:` transcript format the
 * runner used to prepend.
 */
const ROLE_TURN_LINE = /^\s*\[(?:User|Assistant|System)\]\s*:/i;

/**
 * Strip leaked control markup from an assistant text block.
 *
 * Order matters: remove function-call wrappers first (they may span multiple
 * lines and contain role-like text), then drop any trailing fabricated role
 * turns. Only *trailing* role turns are removed — a `[User]:` line in the
 * middle of genuine prose is left alone, because a real answer never emits one
 * mid-stream, whereas the hallucinated follow-up always lands at the end.
 *
 * @param text - Raw assistant text (possibly a partial stream so far).
 * @returns The text with leaked markup removed. Returns the input unchanged
 *   when it contains none.
 */
export function sanitizeAssistantText(text: string): string {
  if (!text || (text.indexOf("[") === -1 && text.indexOf("]") === -1)) {
    return text;
  }

  const stripped = text.replace(FUNCTION_CALL_BLOCK, "").replace(INVOKE_BLOCK, "");
  let changed = stripped !== text;
  let out = stripped;

  // Drop any run of trailing fabricated role turns. Walk lines from the end:
  // once we hit a non-role, non-blank line, the genuine answer has begun and
  // we stop — anything above is real prose and must survive verbatim.
  const lines = out.split("\n");
  let end = lines.length;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      continue; // Trailing blank line — absorbed only if a role turn precedes it.
    }
    if (ROLE_TURN_LINE.test(line)) {
      end = i;
      continue;
    }
    break;
  }
  if (end < lines.length) {
    out = lines.slice(0, end).join("\n");
    changed = true;
  }

  // Only trim the whitespace the removal left dangling; never alter prose that
  // had no leak (a clean answer may legitimately end in a newline).
  return changed ? out.replace(/\s+$/, "") : out;
}

/**
 * Every leakable tag name as a `prefix-OR` alternation, so a *partial* tail
 * (e.g. `[func`, `[inv`, `[Us`) is recognised mid-stream. Each entry lists the
 * progressively-longer prefixes of one name; a bare `[` with nothing after it
 * is also a candidate opener.
 */
const TAG_PREFIXES = [
  "function_call",
  "function_cal",
  "function_ca",
  "function_c",
  "function_",
  "function",
  "functio",
  "functi",
  "funct",
  "func",
  "fun",
  "fu",
  "f",
  "invoke",
  "invok",
  "invo",
  "inv",
  "in",
  "i",
  "user",
  "use",
  "us",
  "u",
  "assistant",
  "assistan",
  "assista",
  "assist",
  "assis",
  "assi",
  "ass",
  "as",
  "a",
  "system",
  "syste",
  "syst",
  "sys",
  "sy",
  "s",
].join("|");

/**
 * A trailing fragment that *could* be the opening (or close) of a leakable
 * marker but is not yet complete: `[`, optional `/`, optional `<ns>:`, then a
 * prefix of a known tag name and no closing `]` yet. Held back while streaming
 * so a marker split across deltas is never half-shown.
 */
const PARTIAL_MARKER_TAIL = new RegExp(`\\[\\s*/?\\s*${NS}(?:${TAG_PREFIXES})?[^\\]]*$`, "i");

/**
 * An *unclosed* opener of a known markup block. {@link sanitizeAssistantText}
 * removes only fully-closed blocks, so any `[function_calls]` or `[invoke …`
 * surviving its pass is necessarily still open — its closing tag has not
 * streamed yet. Hold the text back from that point.
 */
const UNCLOSED_OPENER = new RegExp(`\\[\\s*${NS}function_calls\\s*\\]|\\[\\s*${NS}invoke\\b`, "i");

/**
 * Streaming-safe variant of {@link sanitizeAssistantText}.
 *
 * Returns the sanitized text truncated before (a) any surviving unclosed
 * markup opener and (b) any trailing partial marker fragment, so the caller can
 * emit the result so far without ever flushing the first half of a marker that
 * will complete in a later delta. The held-back portion is re-evaluated on the
 * next call once more text arrives.
 *
 * @param text - The accumulated assistant text so far (not a single delta).
 * @returns The sanitized, complete-marker-free prefix safe to emit now.
 */
export function streamingSafeSanitize(text: string): string {
  let cleaned = sanitizeAssistantText(text);

  const opener = UNCLOSED_OPENER.exec(cleaned);
  if (opener && opener.index >= 0) {
    cleaned = cleaned.slice(0, opener.index);
  }

  const tail = PARTIAL_MARKER_TAIL.exec(cleaned);
  if (tail && tail.index >= 0) {
    cleaned = cleaned.slice(0, tail.index);
  }

  // Always hold back a trailing whitespace run: a fabricated `[User]:` turn or
  // a leaked block may follow it in the next delta, and once a newline has been
  // emitted it cannot be retracted. Withholding it keeps the emitted prefix
  // monotonic with the eventual fully-sanitized text (which trims trailing
  // whitespace). The whitespace flushes naturally once real prose follows it.
  return cleaned.replace(/\s+$/, "");
}
